import clsx from 'clsx';
import React from 'react';
import { Input as ShadcnInput } from '../../shadcn/input';

// The field frame (border, padding, icons) is ours, so the stock input inside it drops its own frame and focus ring.
const bareInput = 'h-auto rounded-none border-0 p-0 shadow-none focus-visible:ring-0';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  size?: 'lg' | 'sm';
  label?: string;
  labelOptional?: boolean;
  assistiveText?: string;
  /** Text shows under the field with a red edge; `true` gives the red edge only, for a message shown elsewhere. */
  error?: string | boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  prefix?: string;
  suffix?: string;
  containerClassName?: string;
  inputClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'lg',
      label,
      labelOptional,
      assistiveText,
      error,
      leftIcon,
      rightIcon,
      prefix,
      suffix,
      containerClassName,
      inputClassName,
      disabled,
      id: idProp,
      ...rest
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const id = idProp ?? generatedId;
    const isLg = size === 'lg';
    const hasError = !!error;
    const errorText = typeof error === 'string' ? error : undefined;

    return (
      <div className={clsx('flex flex-col gap-xs', containerClassName)}>
        {label && (
          <div className="flex items-center justify-between gap-sm">
            <label htmlFor={id} className="text-label-small text-content-dark">
              {label}
            </label>
            {labelOptional && (
              <span className="text-label-small text-content-light">(Optional)</span>
            )}
          </div>
        )}

        <div
          className={clsx(
            'flex items-center border border-solid overflow-hidden',
            isLg ? 'gap-sm p-sm rounded-radius-md' : 'gap-sm px-sm py-xs rounded-radius-sm',
            disabled
              ? 'bg-surface-lighter border-stroke-light cursor-not-allowed'
              : hasError
                ? 'bg-surface-white border-danger-default'
                : 'bg-surface-white border-stroke-light hover:border-stroke-dark focus-within:border-stroke-interactive',
          )}
        >
          {leftIcon && (
            <span className={clsx('shrink-0 flex items-center text-content-dark', isLg ? 'w-5 h-5' : 'w-4 h-4')}>
              {leftIcon}
            </span>
          )}
          {prefix && (
            <span
              className={clsx(
                'shrink-0 text-content-dark',
                isLg ? 'text-body-large' : 'text-body-small',
                disabled && 'opacity-50',
              )}
            >
              {prefix}
            </span>
          )}
          <ShadcnInput
            ref={ref}
            id={id}
            disabled={disabled}
            className={clsx(
              bareInput,
              'flex-1 min-w-0 bg-transparent outline-none',
              isLg ? 'text-body-large' : 'text-body-small',
              'text-content-dark placeholder:text-content-light placeholder:font-normal',
              disabled && 'cursor-not-allowed opacity-50',
              inputClassName,
            )}
            autoComplete="off"
            aria-autocomplete="none"
            spellCheck={false}
            {...rest}
          />
          {suffix && (
            <span
              className={clsx(
                'shrink-0 text-content-dark',
                isLg ? 'text-body-large' : 'text-body-small',
                disabled && 'opacity-50',
              )}
            >
              {suffix}
            </span>
          )}
          {rightIcon && (
            <span className={clsx('shrink-0 flex items-center text-content-dark', isLg ? 'w-5 h-5' : 'w-4 h-4')}>
              {rightIcon}
            </span>
          )}
        </div>

        {(errorText || assistiveText) && (
          <p className={clsx('text-body-small', { 'text-danger-dark': !!errorText, 'text-content-light': !errorText })}>
            {errorText ?? assistiveText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'V2Input';

export { Input };
export type { InputProps as V2InputProps };
